/**
 * fish-permits.js — grass carp Texas permit flow (PondKing.FishPermits).
 * Loaded sitewide from layout/theme.liquid after fish-pickup.js.
 *
 * Grass carp orders must answer "stocking in Texas?" and give the pond
 * address; a Yes also offers the TPWD permit number, pond name, and permit
 * file, all optional. The panel (snippets/fish-grass-carp-permit)
 * renders on the fish order page and inside #CartPageForm; this script:
 *  1. Shows the panel only while grass carp are in the selection (fish page:
 *     rows in tbody[data-fish-grass-carp]) or in the cart (cart page:
 *     #FishCartStatus data-cart-has-grass-carp, kept live by the poller).
 *  2. Records the answer as the cart attribute "Stocking In Texas" (the DOM
 *     radio gates; the attribute is the order record — same split as the
 *     drive-time attestation). Answering No clears any attached permit and
 *     the TX-only fields (the pond address stays).
 *  3. Uploads the permit: POST {filename, contentType, size} to the upload
 *     service (theme setting fish_permit_upload_url) → presigned S3 POST →
 *     saves the retrieval URL as cart attributes "Grass Carp Permit"
 *     (visible on the order) + grass_carp_permit_url (machine).
 *  4. Records the detail fields (data-fish-permit-detail="<attribute name>":
 *     pond address for every answer; permit number / pond name inside
 *     [data-fish-permit-upload], TX=Yes only) as cart attributes on change;
 *     fish-pickup-sync copies them to the order metafields custom.grass_carp_*
 *     for Order Printer. Visible fields carrying data-fish-permit-required
 *     must be filled before checkout.
 *  5. Gates checkout via PondKing.Gate (blocker + submit-time freshness
 *     validator); the fish page's confirm flow calls FishPermits.check().
 *
 * No upload service configured (blank setting) → no file input is rendered.
 */
(function () {
  'use strict';

  window.PondKing = window.PondKing || {};
  if (window.PondKing.FishPermits) return;

  var cfg = window.PondKingFish || {};
  var strings = cfg.strings || {};
  var UPLOAD_URL = (cfg.permitUploadUrl || '').replace(/\/+$/, '');
  var MAX_BYTES = 10 * 1024 * 1024;
  var ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

  // Set after a successful upload this page-view; the Liquid-rendered link
  // carries the value across reloads.
  var uploadedUrl = null;
  var uploading = false;

  function panel() {
    return document.querySelector('[data-fish-grass-carp-panel]');
  }

  function statusNode() {
    var p = panel();
    return p ? p.querySelector('[data-fish-permit-status]') : null;
  }

  function txAnswer() {
    var checked = document.querySelector('[data-fish-tx-radio]:checked');
    return checked ? checked.value : '';
  }

  function detailInputs() {
    var p = panel();
    return p ? p.querySelectorAll('[data-fish-permit-detail]') : [];
  }

  /** TX-only fields live inside the TX=Yes block. */
  function isTexasOnly(input) {
    return !!input.closest('[data-fish-permit-upload]');
  }

  /** Detail inputs that apply to this answer (TX-only ones only on Yes). */
  function activeDetailInputs(answer) {
    var inputs = detailInputs();
    var list = [];
    for (var i = 0; i < inputs.length; i++) {
      if (answer === 'Yes' || !isTexasOnly(inputs[i])) list.push(inputs[i]);
    }
    return list;
  }

  /** Current applicable detail values, keyed by their cart attribute name. */
  function detailValues(answer) {
    var values = {};
    var inputs = activeDetailInputs(answer);
    for (var i = 0; i < inputs.length; i++) {
      values[inputs[i].getAttribute('data-fish-permit-detail')] = inputs[i].value.trim();
    }
    return values;
  }

  /** First applicable required input left blank, or null. */
  function missingRequiredDetail(answer) {
    var inputs = activeDetailInputs(answer);
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].hasAttribute('data-fish-permit-required') && !inputs[i].value.trim()) {
        return inputs[i];
      }
    }
    return null;
  }

  function permitUrl() {
    if (uploadedUrl) return uploadedUrl;
    var p = panel();
    if (!p) return null;
    var wrap = p.querySelector('[data-fish-permit-link-wrap]');
    var link = p.querySelector('[data-fish-permit-link]');
    if (wrap && !wrap.classList.contains('hide') && link && link.getAttribute('href')) {
      return link.getAttribute('href');
    }
    return null;
  }

  /* ---------------- grass carp presence ---------------- */

  function grassCarpSelected() {
    // Fish page: any positive selection whose row lives in a grass carp
    // tbody. Selections are the source of truth (inputs may be mid-edit).
    var groups = document.querySelectorAll('tbody[data-fish-grass-carp]');
    if (!groups.length) return false;
    for (var g = 0; g < groups.length; g++) {
      var inputs = groups[g].querySelectorAll('input[data-fish-qty]');
      for (var i = 0; i < inputs.length; i++) {
        var value = parseInt(inputs[i].value, 10);
        if (!isNaN(value) && value > 0) return true;
      }
    }
    return false;
  }

  function grassCarpInCart() {
    var el = document.getElementById('FishCartStatus');
    return !!el && el.getAttribute('data-cart-has-grass-carp') === 'true';
  }

  function active() {
    if (!panel()) return false;
    return grassCarpSelected() || grassCarpInCart();
  }

  /* ---------------- panel visibility ---------------- */

  function refreshPanel() {
    var p = panel();
    if (!p) return;
    p.classList.toggle('hide', !active());
    var upload = p.querySelector('[data-fish-permit-upload]');
    if (upload) upload.classList.toggle('hide', txAnswer() !== 'Yes');
    if (window.PondKing.Gate) window.PondKing.Gate.evaluate();
  }

  /* ---------------- cart attribute writes ---------------- */

  function saveAttributes(attributes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart attribute save ' + r.status);
      return r;
    });
  }

  function clearPermitAttributes() {
    uploadedUrl = null;
    var p = panel();
    if (p) {
      var wrap = p.querySelector('[data-fish-permit-link-wrap]');
      if (wrap) wrap.classList.add('hide');
      var file = p.querySelector('[data-fish-permit-file]');
      if (file) file.value = '';
    }
    showStatus('', false);
    var attributes = { grass_carp_permit_url: '' };
    attributes['Grass Carp Permit'] = '';
    var inputs = detailInputs();
    for (var i = 0; i < inputs.length; i++) {
      if (!isTexasOnly(inputs[i])) continue;
      inputs[i].value = '';
      attributes[inputs[i].getAttribute('data-fish-permit-detail')] = '';
    }
    return saveAttributes(attributes).catch(function () { /* swept later */ });
  }

  /* ---------------- upload flow ---------------- */

  function showStatus(message, isError) {
    var node = statusNode();
    if (!node) return;
    // Language-editor-edited strings can carry HTML entities; normalize for
    // textContent rendering (same pattern as fish-table's toPlainText).
    if (message && message.indexOf('&') !== -1) {
      var tmp = document.createElement('div');
      tmp.innerHTML = message;
      message = tmp.textContent || '';
    }
    node.textContent = message || '';
    node.classList.toggle('fish-grass-carp__status--error', !!isError);
  }

  function fileError(message) {
    uploading = false;
    showStatus(message, true);
  }

  function onFileChosen(input) {
    var file = input.files && input.files[0];
    if (!file || !UPLOAD_URL || uploading) return;
    // Some browsers report an empty type for PDFs dragged from odd sources —
    // fall back to the extension.
    var type = file.type;
    if (!type) {
      var ext = (file.name.split('.').pop() || '').toLowerCase();
      type = { pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png' }[ext] || '';
    }
    if (ALLOWED_TYPES.indexOf(type) === -1) {
      fileError(strings.permitFileType || 'Please attach a PDF or photo (JPG/PNG).');
      return;
    }
    if (file.size > MAX_BYTES) {
      fileError(strings.permitFileSize || 'That file is too large — the limit is 10 MB.');
      return;
    }

    uploading = true;
    showStatus(strings.permitUploading || 'Uploading your permit…', false);

    fetch(UPLOAD_URL + '/permits', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, contentType: type, size: file.size })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('presign ' + r.status);
        return r.json();
      })
      .then(function (grant) {
        if (!grant || !grant.upload || !grant.upload.url || !grant.viewUrl) {
          throw new Error('bad presign response');
        }
        var body = new FormData();
        var fields = grant.upload.fields || {};
        for (var key in fields) {
          if (Object.prototype.hasOwnProperty.call(fields, key)) body.append(key, fields[key]);
        }
        body.append('file', file); // must be last for S3 POST policies
        return fetch(grant.upload.url, { method: 'POST', body: body }).then(function (r) {
          if (!r.ok) throw new Error('s3 upload ' + r.status);
          return grant.viewUrl;
        });
      })
      .then(function (viewUrl) {
        var attributes = { grass_carp_permit_url: viewUrl };
        attributes['Grass Carp Permit'] = viewUrl;
        return saveAttributes(attributes).then(function () { return viewUrl; });
      })
      .then(function (viewUrl) {
        uploading = false;
        uploadedUrl = viewUrl;
        showStatus(strings.permitUploaded || 'Permit attached', false);
        var p = panel();
        if (p) {
          var link = p.querySelector('[data-fish-permit-link]');
          var wrap = p.querySelector('[data-fish-permit-link-wrap]');
          if (link) link.setAttribute('href', viewUrl);
          if (wrap) wrap.classList.remove('hide');
        }
        if (window.PondKing.Gate) window.PondKing.Gate.evaluate();
      })
      .catch(function () {
        fileError(strings.permitUploadFailed || 'Could not upload your permit. Please try again.');
      });
  }

  /* ---------------- checkout gating ---------------- */

  // Shared verdict: null when fine, {message} when blocked. Used by the cart
  // gate blocker and the fish page's confirm flow (FishPermits.check).
  function verdict() {
    if (!active()) return null;
    var answer = txAnswer();
    if (!answer) {
      return { message: strings.txQuestionRequired || "Please tell us whether you're stocking these grass carp in Texas." };
    }
    var missing = missingRequiredDetail(answer);
    if (missing) {
      if (isTexasOnly(missing)) {
        return { message: strings.permitDetailsRequired || 'Please enter your TPWD permit number.' };
      }
      return { message: strings.pondAddressRequired || 'Please enter the address of the pond being stocked.' };
    }
    if (uploading) {
      return { message: strings.permitUploading || 'Uploading your permit…' };
    }
    return null;
  }

  function registerGate() {
    if (!window.PondKing.Gate) return;

    window.PondKing.Gate.addBlocker('grass-carp-permit', function () {
      return verdict();
    });

    // The blocker trusts the DOM; this re-checks the CART at the moment of
    // submission — the attributes are what actually land on the order.
    window.PondKing.Gate.addValidator('grass-carp-permit-fresh', function () {
      if (!active()) return Promise.resolve(null);
      var answer = txAnswer();
      if (!answer || missingRequiredDetail(answer)) {
        return Promise.resolve(verdict());
      }
      return fetch('/cart.js?_fts=' + Date.now(), { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var attrs = cart.attributes || {};
          // Re-save any attribute the cart disagrees with rather than fail —
          // a change handler's write may have been lost (offline blip); the
          // native inputs also post them.
          var resave = {};
          var stale = false;
          if (attrs['Stocking In Texas'] !== answer) {
            resave['Stocking In Texas'] = answer;
            stale = true;
          }
          var details = detailValues(answer);
          for (var name in details) {
            if (Object.prototype.hasOwnProperty.call(details, name) &&
                (attrs[name] || '') !== details[name]) {
              resave[name] = details[name];
              stale = true;
            }
          }
          if (stale) {
            if (answer === 'Yes' && permitUrl()) {
              resave.grass_carp_permit_url = permitUrl();
              resave['Grass Carp Permit'] = permitUrl();
            }
            return saveAttributes(resave).then(function () { return null; });
          }
          // Optional file: re-attach one uploaded this page-view if its save
          // was lost, but never block without one.
          var url = answer === 'Yes' ? permitUrl() : null;
          if (url && !attrs.grass_carp_permit_url) {
            var reattach = { grass_carp_permit_url: url };
            reattach['Grass Carp Permit'] = url;
            return saveAttributes(reattach).then(function () { return null; });
          }
          return null;
        });
    });
  }

  /* ---------------- listeners ---------------- */

  document.addEventListener('change', function (evt) {
    var target = evt.target;
    if (!target || !target.hasAttribute) return;

    if (target.hasAttribute('data-fish-tx-radio')) {
      var attributes = {};
      attributes['Stocking In Texas'] = target.value;
      saveAttributes(attributes).catch(function () { /* validator re-saves */ });
      if (target.value === 'No') clearPermitAttributes();
      refreshPanel();
      return;
    }

    if (target.hasAttribute('data-fish-permit-detail')) {
      var detail = {};
      detail[target.getAttribute('data-fish-permit-detail')] = target.value.trim();
      saveAttributes(detail).catch(function () { /* validator re-saves */ });
      if (window.PondKing.Gate) window.PondKing.Gate.evaluate();
      return;
    }

    if (target.hasAttribute('data-fish-permit-file')) {
      onFileChosen(target);
    }
  });

  // Selection edits (fish page) and section swaps (both pages) can change
  // whether grass carp are in play.
  document.addEventListener('input', function (evt) {
    var target = evt.target;
    if (!target || !target.hasAttribute) return;
    if (target.hasAttribute('data-fish-permit-detail')) {
      // Let the blocker's message clear as they type (attribute saves on change).
      if (window.PondKing.Gate) window.PondKing.Gate.evaluate();
      return;
    }
    if (!target.hasAttribute('data-fish-qty')) return;
    refreshPanel();
  });
  document.addEventListener('fish:refreshed', refreshPanel);

  function init() {
    registerGate();
    refreshPanel();
  }

  window.addEventListener('pageshow', function (evt) {
    if (evt.persisted) refreshPanel();
  });

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  window.PondKing.FishPermits = {
    check: verdict,
    refresh: refreshPanel
  };
})();
